/**
 * GuidancePanel - "Mózg ekranu" - True hand-holding assistant
 * Structure: STATUS → CO TERAZ → CTA → POSTĘP → PO CO
 * 
 * PREMIUM VERSION V4 - Final polish with proper statuses
 */
import React from 'react';
import { Check, ChevronRight, Lightbulb, Lock, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Timeline checklist item - with "inprogress" status
function ChecklistItem({ label, status, isLast }) {
  const configs = {
    done: { 
      dot: 'bg-green-500',
      line: 'bg-green-300',
      text: 'text-gray-500',
      decoration: 'line-through',
      icon: <Check className="w-3 h-3 text-white" strokeWidth={3} />
    },
    current: { 
      dot: 'bg-orange-600 ring-[3px] ring-orange-200',
      line: 'bg-gray-200',
      text: 'text-orange-700 font-semibold',
      decoration: '',
      icon: null
    },
    inprogress: { 
      dot: 'bg-amber-500',
      line: 'bg-amber-200',
      text: 'text-amber-700 font-medium',
      decoration: '',
      icon: null
    },
    warning: { 
      dot: 'bg-amber-500',
      line: 'bg-gray-200',
      text: 'text-amber-700',
      decoration: '',
      icon: null
    },
    pending: { 
      dot: 'bg-gray-200',
      line: 'bg-gray-200',
      text: 'text-gray-400',
      decoration: '',
      icon: null
    }
  };
  
  const config = configs[status] || configs.pending;
  
  return (
    <div className="flex gap-3">
      {/* Timeline column - separate from text */}
      <div className="flex flex-col items-center w-6 flex-shrink-0">
        <div className={`w-5 h-5 rounded-full flex items-center justify-center ${config.dot}`}>
          {config.icon}
        </div>
        {!isLast && <div className={`w-0.5 flex-1 mt-1 ${config.line}`} style={{ minHeight: '20px' }} />}
      </div>
      {/* Label - clear separation */}
      <span className={`text-sm leading-5 pb-3 ${config.text} ${config.decoration}`}>
        {label}
      </span>
    </div>
  );
}

export default function GuidancePanel({
  // Status
  stepLabel = 'Krok 1 z 4',
  statusBadge = 'Szkic',
  
  // "Co teraz?" - HERO
  whatNow = '',
  
  // Validation
  validationError = null,
  canProceed = true,
  
  // Primary CTA
  ctaText = 'Dalej',
  ctaAction = null,
  ctaDisabled = false,
  ctaLoading = false,
  
  // Checklist
  checklist = [],
  
  // Summary (for Step 4)
  summary = null,
  
  // "Po co?"
  whyText = '',
  
  // Tip
  tipText = null,
  
  children
}) {
  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-2xl shadow-gray-300/40 border border-gray-100">
      {/* HEADER */}
      <div className="px-6 py-4 bg-gradient-to-r from-orange-600 via-orange-600 to-purple-700">
        <div className="flex items-center justify-between">
          <span className="text-orange-100 text-sm font-medium">{stepLabel}</span>
          <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-white text-xs font-bold">
            {statusBadge}
          </span>
        </div>
      </div>
      
      {/* CONTENT */}
      <div className="p-6">
        
        {/* 1. CO TERAZ? - HERO with more padding */}
        <div className="mb-7">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-2.5 h-2.5 bg-orange-500 rounded-full animate-pulse" />
            <span className="text-xs font-bold text-orange-600 uppercase tracking-wider">
              Co teraz?
            </span>
          </div>
          <div className="bg-gradient-to-br from-orange-50 via-purple-50/80 to-orange-50 rounded-xl p-6 border border-orange-200/60">
            <p className="text-base font-semibold text-gray-900 leading-relaxed">
              {whatNow}
            </p>
          </div>
        </div>
        
        {/* 2. CTA */}
        {ctaAction && (
          <div className="mb-7">
            <Button
              onClick={ctaAction}
              disabled={ctaDisabled || ctaLoading}
              className={`
                w-full h-14 text-base font-bold rounded-xl transition-all
                ${ctaDisabled 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-2 border-gray-200' 
                  : 'bg-gradient-to-r from-orange-600 to-orange-600 hover:from-orange-700 hover:to-purple-700 text-white shadow-lg shadow-orange-400/30 hover:shadow-orange-500/40 hover:scale-[1.01]'
                }
              `}
            >
              {ctaLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Generuję plan...
                </span>
              ) : ctaDisabled ? (
                <span className="flex items-center justify-center gap-2">
                  <Lock className="w-4 h-4" />
                  {ctaText}
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  {ctaText}
                  <ChevronRight className="w-5 h-5" />
                </span>
              )}
            </Button>
            
            {/* Validation message UNDER CTA - subtle amber */}
            {validationError && ctaDisabled && (
              <div className="flex items-center gap-2 mt-3 py-2 px-3.5 bg-amber-50/80 rounded-lg border border-amber-100">
                <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                <p className="text-sm text-amber-700">{validationError}</p>
              </div>
            )}
          </div>
        )}
        
        {/* SUMMARY (Step 4 parameters) */}
        {summary && (
          <div className="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-100">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
              Twój plan
            </h3>
            <ul className="space-y-1.5 text-sm text-gray-700">
              {summary.map((item, i) => (
                <li key={i} className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {/* DIVIDER */}
        <div className="h-px bg-gray-100 mb-6" />
        
        {/* 3. POSTĘP - Timeline checklist - stronger header */}
        {checklist.length > 0 && (
          <div className="mb-6">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4">
              Postęp
            </h3>
            <div className="pl-1">
              {checklist.map((item, i) => (
                <ChecklistItem 
                  key={i} 
                  label={item.label} 
                  status={item.status}
                  isLast={i === checklist.length - 1}
                />
              ))}
            </div>
          </div>
        )}
        
        {/* DIVIDER */}
        <div className="h-px bg-gray-100 mb-6" />
        
        {/* 4. PO CO? - stronger contrast */}
        {whyText && (
          <div className="mb-4">
            <h3 className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-2">
              Po co to robisz?
            </h3>
            <p className="text-sm text-gray-600 leading-relaxed">{whyText}</p>
          </div>
        )}
        
        {/* 5. TIP */}
        {tipText && (
          <div className="flex items-start gap-3 p-4 bg-amber-50/70 rounded-xl border border-amber-100">
            <Lightbulb className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">{tipText}</p>
          </div>
        )}
        
        {children}
      </div>
    </div>
  );
}
